import { NavLink } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuthState } from '../lib/auth';
import GlobalSearch from './GlobalSearch';
import {
  LayoutDashboard,
  BarChart3,
  DollarSign,
  Map,
  Settings,
  Boxes,
  ToggleLeft,
  PackageSearch,
  Truck,
  Users,
  UserCircle,
  Receipt,
  ScrollText,
  Activity,
  Inbox,
  LogOut,
} from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', Icon: LayoutDashboard },
      { to: '/inbox', label: 'Inbox', Icon: Inbox },
      { to: '/analytics', label: 'Analytics', Icon: BarChart3 },
      { to: '/monitoring', label: 'Monitoring', Icon: Activity },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/orders', label: 'Orders', Icon: PackageSearch },
      { to: '/jobs', label: 'Jobs', Icon: Truck },
      { to: '/refunds', label: 'Refunds', Icon: Receipt },
    ],
  },
  {
    title: 'People',
    items: [
      { to: '/drivers', label: 'Drivers', Icon: UserCircle },
      { to: '/customers', label: 'Customers', Icon: Users },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { to: '/pricing', label: 'Pricing', Icon: DollarSign },
      { to: '/service-area', label: 'Service Area', Icon: Map },
      { to: '/operations', label: 'Operations', Icon: Settings },
      { to: '/catalogs', label: 'Catalogs', Icon: Boxes },
      { to: '/feature-flags', label: 'Feature Flags', Icon: ToggleLeft },
    ],
  },
  {
    title: 'System',
    items: [{ to: '/audit', label: 'Audit Log', Icon: ScrollText }],
  },
];

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuthState();
  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="sticky top-0 flex h-screen w-60 flex-col border-r border-slate-200 bg-white">
        <div className="flex items-center gap-2.5 px-4 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500 text-white shadow-sm">
            <Truck size={20} />
          </div>
          <div>
            <div className="text-base font-bold leading-tight text-slate-900">
              DeliveryFor
            </div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-brand-600">
              Admin Portal
            </div>
          </div>
        </div>
        <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
          {NAV_SECTIONS.map(({ title, items }) => (
            <div key={title}>
              <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {title}
              </div>
              <div className="space-y-0.5">
                {items.map(({ to, label, Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition ${
                        isActive
                          ? 'bg-brand-50 font-semibold text-brand-700'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    <Icon size={16} className="shrink-0" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3 text-xs">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold uppercase text-brand-700">
              {user?.email?.[0] ?? '?'}
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium text-slate-700">
                {user?.email}
              </div>
              <div className="text-[11px] text-slate-400">Administrator</div>
            </div>
          </div>
          <button
            onClick={() => signOut(auth)}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 px-2 py-1.5 font-medium text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 px-6 py-3 backdrop-blur lg:px-8">
          <div className="mx-auto max-w-6xl">
            <GlobalSearch />
          </div>
        </div>
        <div className="mx-auto max-w-6xl p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
